import { ModifierType } from './api/menu.types';
import { WeekDay } from './location.types';
import { Provider } from './shared.types';
import { TaxRateEntity } from './taxRate.types';

export type SimpleMenuProviderEntity = {
  /** Optional until every response that carries this shape sends it. */
  id?: number;
  name: string;
  image: string | null;
  /** The Manage Providers marketplace (`ubereats`, ...); null on a legacy row. */
  type?: string | null;
};

export interface MenuInfo {
  manager?: string | null;
  assistant?: string | null;
  pictureAssistant?: string | null;
}

export enum DpType {
  SubMenu = 'SubMenu',
  Item = 'Item',
  PriceGroup = 'PriceGroup',
  OptionGroup = 'OptionGroup',
  Option = 'Option',
  Image = 'Image',
}

export enum ItemType {
  Category = 'category',
  Item = 'item',
  ModifierGroup = 'modifierGroup',
  // Modifier = 'modifier',
  // Image = 'image',
}

export enum SelectionType {
  Radio = 'radio',
  Check = 'check',
  Quantity = 'quantity',
  UpToOne = 'upToOne',
  OnlyOne = 'onlyOne',
  MinMax = 'minMax',
}

export type MenuAvailabilityHourV2Response = {
  day: WeekDay;
  id?: number | null;
  startTime: string;
  endTime: string;
};

export type MenuAvailabilityV2Response = {
  id: number;
  name: string;
  hours: MenuAvailabilityHourV2Response[];
};

export type MenuPriceCustomizationV2Response = {
  provider: Provider;
  price: number;
  pickupPrice?: number | null;
};

export type MenuOptionPriceCustomizationV2Response = {
  provider: Provider;
  price: number;
};

export type ModifierItemV2Response = {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  availability: Availability;
  /** See `MenuItemV2Response.linkKey`. */
  linkKey?: string | null;
  posId?: number | null;
  modifierGroups: ModifierGroupItemV2Response[];
  customizedPrices?: MenuOptionPriceCustomizationV2Response[];
  /**
   * 1-based position in its group, for display only: `options` already arrive
   * in this order, so never sort them on the client. 0 on options created
   * before option ordering until the backend migration runs.
   */
  order?: number;
  /**
   * The taxes this option is published with - the effective list, not the
   * assignment. When `taxRatesOverridden` is false these are the location's
   * `applyToAllItems` rates; when it is true they are the option's own choice.
   *
   * Optional because a backend without the tax rates release omits both.
   */
  taxRates?: TaxRateEntity[];
  taxRatesOverridden?: boolean;
};

export enum Availability {
  Available = 'available',
  SoldOut = 'soldOut',
}

export type ModifierGroupItemV2Response = {
  name: string;
  type: ModifierType;
  id: number;
  description?: string | null;
  required: boolean;
  selectionsMin: number;
  selectionsMax: number;
  options: ModifierItemV2Response[];
  posId?: number | null;
};

export type MenuItemV2Response = {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  pickupPrice?: number | null;
  customizedPrices: MenuPriceCustomizationV2Response[];
  imageUrl: string;
  modifierGroups: ModifierGroupItemV2Response[];
  availability: Availability;
  preparationNotes?: string | null;
  order?: number;
  /** Now only "the pause has an end" - read `unpauseAt` for when. */
  pausedUntilEndOfTheDay: boolean;
  /**
   * When a sold out item comes back by itself (ISO, within ~5 min after).
   * Null when available or paused with no end; absent on an API build older
   * than custom pauses.
   */
  unpauseAt?: string | null;
  allowedToEditMenuPrice: boolean;
  /**
   * Same product in other menus of the brand. Items sharing a `linkKey` share
   * their availability (sold out in one is sold out in all). Only copying a
   * menu creates links; `null` means the item is linked to nothing.
   */
  linkKey?: string | null;
  /** Effective taxes and whether they are the item's own. See the option. */
  taxRates?: TaxRateEntity[];
  taxRatesOverridden?: boolean;
};

export type CategoryV2Response = {
  id: number;
  name: string;
  description?: string | null;
  schedules: MenuAvailabilityV2Response[];
  items: MenuItemV2Response[];
  posId?: number | null;
};

// export type MenuItemResponse = {
//   name: string;
//   dpType: DpType;
//   type: ItemType;
//   price: number | null;
//   pickupPrice: number | null;
//   id: string;
//   entityId: string;
//   description: string | null;
//   qtyIncrements: number | null;
//   qtyMin: number | null;
//   qtyMax: number | null;
//   selectionsMin: number | null;
//   selectionsMax: number | null;
//   selectionsDepends: boolean | null;
//   selectionType: SelectionType | null;
//   children: [];
//   availability: MenuAvailabilityV2Response[];
//   priceCustomization: MenuPriceCustomizationV2Response[];
//   imageUrl: string | null;
//   imageId: number | null;
// };

export type MenuV2Response = {
  categories: CategoryV2Response[];
  modifierGroups: ModifierGroupItemV2Response[];
  availability: MenuAvailabilityV2Response[];
  // Which of the brand's menus this is. Optional because import previews and
  // publish-history snapshots reuse the shape without them.
  menuId?: number;
  menuName?: string;
  isDefault?: boolean;
};

export type ImportMenuUrlPreviewV2Response = {
  started: boolean;
  finished: boolean;
  menu: null | MenuV2Response;
};

export const daysOfWeekStringV2 = [
  { name: 'Sunday', value: WeekDay.Sunday },
  { name: 'Monday', value: WeekDay.Monday },
  { name: 'Tuesday', value: WeekDay.Tuesday },
  { name: 'Wednesday', value: WeekDay.Wednesday },
  { name: 'Thursday', value: WeekDay.Thursday },
  { name: 'Friday', value: WeekDay.Friday },
  { name: 'Saturday', value: WeekDay.Saturday },
];

export type UpdateMenuListOrders = {
  id: number;
  order: number;
};

export type ImportItemsFromPosMenuDto = {
  categoryIds: number[];
};

export type UpdateMenuPosModifierOptionDto = {
  price: number;
  customizedPrices?: MenuOptionPriceCustomizationV2Response[];
};
